import { Last7DaysMode } from "constants/reports/last-7-days-mode";
import { ReportType } from "constants/reports/report-type";
import { KeyMap } from "utils/interface";
import { capitalizeFirstLetter } from "utils/string";

import { EventPlace, trackEngagement } from "./analytics";

declare const App: {
  views: {
    reports: {
      contentView: {
        getCurrentViewId(): string;
      };
    };
  };
};

interface IReportViewDetails {
  name: string;
  category: string;
}

const reportViewsDetails: KeyMap<IReportViewDetails> = {
  [ReportType.Last7days]: {
    name: "Last 7 days",
    category: "Last 7 days",
  },
  dashboard: {
    name: "Dashboard",
    category: "Last 7 days",
  },
  [ReportType.NewTickets]: {
    name: "New tickets",
    category: "Tickets",
  },
  [ReportType.TicketRatings]: {
    name: "Ticket satisfaction",
    category: "Tickets",
  },
  [ReportType.TicketFirstResponseTime]: {
    name: "First response time",
    category: "Tickets",
  },
  [ReportType.SolvedAndClosed]: {
    name: "Solved and closed",
    category: "Tickets",
  },
  [ReportType.TicketResolutionTime]: {
    name: "Resolution time",
    category: "Tickets",
  },
  "ticket-sources": {
    name: "Ticket sources",
    category: "Tickets",
  },
  [ReportType.ScheduledReports]: {
    name: "Scheduled Reports",
    category: "Export",
  },
  "generate-report": {
    name: "Generate report",
    category: "Export",
  },
  [ReportType.HandlingTimeByStatus]: {
    name: "Handling time by status",
    category: "Tickets",
  },
};

export function trackDistributionChange(wasDisabled: boolean, reportName: string | ReportType): void {
  trackEngagement("Reports: 24-hour distribution toggled", EventPlace.Reports, {
    state: wasDisabled ? "enabled" : "disabled",
    report: reportName,
  });
}

export function trackEmptyStateClicked(emptyStateType: string): void {
  trackEngagement("Reports: Empty state clicked", EventPlace.Reports, {
    "empty state": emptyStateType,
  });
}

export function trackDatapointClicked(chartName: string): void {
  trackEngagement("Reports: Data point clicked", EventPlace.Reports, {
    type: chartName,
  });
}

export function trackSaveAsCSV(chartName: string): void {
  trackEngagement("Reports: Report saved as CSV", EventPlace.Reports, {
    report: chartName,
  });
}

export function trackReportExported(dataSource: string, type: string, dateRange: string, format: string): void {
  trackEngagement("Reports: Report exported", EventPlace.Reports, {
    "data source": dataSource,
    type,
    "date-range": dateRange,
    format,
  });
}

export function trackReportAccessed(categoryName: string, reportName: string, source?: string): void {
  trackEngagement(`Reports: ${capitalizeFirstLetter(categoryName)} report accessed`, EventPlace.Reports, {
    report: reportName,
    ...(source && { source }),
  });
}

export function trackCategoryAccessed(categoryName: string): void {
  trackEngagement("Reports: Report category opened", EventPlace.Reports, {
    category: categoryName,
  });
}

export function trackLast7DaysSummaryReportClicked(reportName: string | ReportType, mode?: Last7DaysMode): void {
  trackEngagement("Reports: Last 7 days summary report clicked", EventPlace.Reports, {
    report: reportName,
    presentedData: mode,
  });
}

export function trackLast7DaysEmptyStateClicked(reportName: string | ReportType): void {
  trackEngagement("Reports: Last 7 days empty state clicked", EventPlace.Reports, {
    report: reportName,
  });
}

export function trackUpgradeButtonClicked(reportName: string | ReportType): void {
  trackEngagement("Reports: Upgrade button clicked", EventPlace.Reports, {
    report: reportName,
  });
}
export function trackAgentProfileClicked(reportName: string | ReportType): void {
  trackEngagement("Reports: Agent profile clicked", EventPlace.Reports, {
    report: reportName,
  });
}

export function trackScheduledReportAction(actionName: string): void {
  trackEngagement("Reports: Scheduled report button clicked", EventPlace.Reports, { action: actionName });
}

export function trackRatingRankingClicked(reportName: string | ReportType, rate: string | ReportType): void {
  trackEngagement("Reports: Rating ranking clicked", EventPlace.Reports, {
    report: reportName,
    rate,
  });
}

export function trackDashboardOpened(): void {
  trackEngagement("Reports: Dashboard opened", EventPlace.Reports);
}

export function trackFiltersChange(
  reportName: string,
  filterType: string,
  concurrent: number,
  comparison?: string
): void {
  trackEngagement("Reports: Report filtered", EventPlace.Reports, {
    report: reportName,
    "filter type": filterType,
    "concurrent filters": concurrent,
    ...(comparison
      ? {
          comparison: "true",
          "comparison type": comparison,
        }
      : {
          comparison: "false",
        }),
  });
}

export function trackChartLegendClick(chartName: string, legend: string): void {
  trackEngagement("Reports: Chart legend clicked", EventPlace.Reports, {
    "chart name": chartName,
    legend,
  });
}

export function getCurrentReportTrackingDetails(
  reportViewId: string = App.views.reports.contentView.getCurrentViewId()
): IReportViewDetails | false {
  return reportViewsDetails[reportViewId] || false;
}

export function trackUpsellViewDisplayed(report: string): void {
  trackEngagement("Reports: Up-selling viewed", EventPlace.Reports, {
    report,
  });
}

export function getTargetByPathname(href: string): string {
  if (href.startsWith("https://www.livechatinc.com/kb/")) {
    return "KB";
  }

  if (href.startsWith("https://www.livechatinc.com/applications/")) {
    return "Applications page";
  }

  if (href.startsWith("https://www.livechatinc.com/marketplace")) {
    return "Marketplace";
  }

  if (href.startsWith("https://www.livechatinc.com/livechat-resources")) {
    return "Resources";
  }

  if (/\/tickets\/add/.test(href)) {
    return "New ticket";
  }

  if (/\/settings\//.test(href)) {
    return "Settings";
  }

  return href;
}

export function trackTooltipCTAClicked(report: string, href: string): void {
  trackEngagement("Reports: Tooltip CTA clicked", EventPlace.Reports, {
    report,
    target: getTargetByPathname(href),
  });
}

export function trackChatSurveyEditClicked(): void {
  trackEngagement("Reports: Chat survey report edit", EventPlace.Reports);
}

export function trackChatSurveyToggled(): void {
  trackEngagement("Reports: Chat survey report toggled", EventPlace.Reports);
}

export function trackInactiveAgentsToggled(enabled: boolean): void {
  trackEngagement("Reports: Hide inactive agents toggled", EventPlace.Reports, {
    "target state": enabled ? "enabled" : "disabled",
  });
}

export function trackDaySwitchClicked(direction: string): void {
  trackEngagement("Reports: Switched between days", EventPlace.Reports, {
    direction,
  });
}

export function trackInfoButtonHovered(chartName: string): void {
  trackEngagement("Reports: Info button hovered over", EventPlace.Reports, {
    report: chartName,
  });
}

export function trackHoveredHeatmap(heatmapName: string): void {
  trackEngagement("Reports: Hovered on heatmap", EventPlace.Reports, {
    report: capitalizeFirstLetter(heatmapName),
  });
}
